import React from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Home,
  Image as ImageIcon,
  Info,
  Landmark,
  LoaderCircle,
  MapPin,
  Search,
  Trash2,
  UploadCloud,
  X,
  Zap,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./LenderManualLoanCasePropertyInfo.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const MAX_BUILD_YEAR = new Date().getFullYear();
const MIN_BUILD_YEAR = 1800;

const EMPTY_PROPERTY_INFO = {
  id: null,
  property_type: "",
  usable_area_sqm: "",
  year_built: "",
  building_condition: "",
  has_lift: "",
  comfort_type: "",
  notes: "",
  street: "",
  house_number: "",
  city: "",
  postal_code: "",
  country_code: "",
  apartment_number: "",
  region: "",
  full_address: "",
  latitude: "",
  longitude: "",
  place_id: "",
  epc_label: "",
  epc_target_label: "",
  epc_value: "",
  epc_target_value: "",
  heating_type: "",
  cooling_type: "",
  land_register_id: "",
  ownership_type: "",
  photos: [],
};

const REQUIRED_FIELDS = new Set([
  "property_type",
  "building_condition",
  "usable_area_sqm",
  "year_built",

  "epc_label",
  "epc_value",

  "heating_type",
  "cooling_type",
  "ownership_type",

  "street",
  "city",
  "postal_code",
]);

const OTHER_FIELDS = new Set(["property_type", "ownership_type", "cooling_type"]);

const INTEGER_FIELDS = new Set([]);

const DECIMAL_FIELDS = new Set([
  "usable_area_sqm",
  "epc_value",
  "epc_target_value",
]);

const FIELD_LABEL_FALLBACKS = {
  property_type: "Property type",
  usable_area_sqm: "Surface (m²)",
  year_built: "Year of build",
  has_lift: "Lift",
  building_condition: "Building condition",
  comfort_type: "Comfort type",
  notes: "Notes",
  epc_label: "EPC rating",
  epc_target_label: "EPC rating after renovation (target)",
  epc_value: "kWh/m² per year (current)",
  epc_target_value: "kWh/m² per year (target)",
  heating_type: "Type of heating",
  cooling_type: "Type of cooling",
  land_register_id: "Land register ID",
  ownership_type: "Ownership type",
  street: "Street name",
  house_number: "House number",
  city: "City",
  postal_code: "ZIP code",
  country_code: "Country",
  apartment_number: "Apartment number",
};

const FIELD_TOOLTIP_KEYS = {
  property_type: "descriptions.propertyType",
  usable_area_sqm: "descriptions.usableArea",
  year_built: "descriptions.yearBuilt",
  has_lift: "descriptions.hasLift",
  comfort_type: "descriptions.comfortType",
  building_condition: "descriptions.buildingCondition",
  notes: "descriptions.notes",
  epc_label: "descriptions.epcCurrent",
  epc_target_label: "descriptions.epcTarget",
  epc_value: "descriptions.epcValueCurrent",
  epc_target_value: "descriptions.epcValueTarget",
  heating_type: "descriptions.heatingType",
  cooling_type: "descriptions.coolingType",
  land_register_id: "descriptions.landRegisterId",
  ownership_type: "descriptions.ownershipType",
  street: "descriptions.street",
  house_number: "descriptions.houseNumber",
  city: "descriptions.city",
  postal_code: "descriptions.postalCode",
  country_code: "descriptions.country",
  apartment_number: "descriptions.apartmentNumber",
};

const FIELD_TOOLTIP_FALLBACKS = {
  property_type: "Type of residential dwelling.",
  usable_area_sqm: "Total usable floor area of the property in square metres.",
  year_built: "Approximate year in which the building was constructed.",
  has_lift: "Indicates whether the building has an elevator.",
  comfort_type:
    "Overall comfort level of the dwelling, including insulation, heating, ventilation and indoor comfort.",
  notes:
    "Additional information about the property that may be relevant for assessment.",
  building_condition: "Current overall physical condition of the property.",
  epc_label:
    "Current energy-efficiency class stated in the Energy Performance Certificate.",
  epc_target_label: "Expected energy-efficiency class after renovation.",
  epc_value:
    "Current annual energy consumption per square metre stated in the EPC.",
  epc_target_value:
    "Expected annual energy consumption per square metre after renovation.",
  heating_type: "Primary heating system currently installed.",
  cooling_type: "Cooling system currently installed, if any.",
  land_register_id:
    "Official property identification number in the national land registry or cadastre.",
  ownership_type:
    "Indicates whether the applicant is sole owner, co-owner or has another ownership arrangement.",
  street: "Street where the property is located.",
  house_number: "Building number on the street.",
  city: "City or municipality where the property is located.",
  postal_code: "Postal code of the property location.",
  country_code: "Country where the property is located.",
  apartment_number:
    "Apartment or unit number within the building, when applicable.",
};

const PROPERTY_TYPE_OPTIONS = [
  ["APARTMENT_FLAT", "options.propertyType.apartmentFlat", "Apartment / Flat"],
  [
    "STUDIO_APARTMENT",
    "options.propertyType.studioApartment",
    "Studio apartment",
  ],
  [
    "SINGLE_FAMILY_HOUSE_DETACHED",
    "options.propertyType.singleFamilyHouseDetached",
    "Single-family house (detached)",
  ],
  [
    "SEMI_DETACHED_HOUSE",
    "options.propertyType.semiDetachedHouse",
    "Semi-detached house",
  ],
  [
    "TERRACED_ROW_HOUSE",
    "options.propertyType.terracedRowHouse",
    "Terraced / Row house",
  ],
  [
    "DUPLEX_MAISONETTE",
    "options.propertyType.duplexMaisonette",
    "Duplex / Maisonette",
  ],
  ["TOWNHOUSE", "options.propertyType.townhouse", "Townhouse"],
  [
    "MULTI_FAMILY_RESIDENTIAL_BUILDING",
    "options.propertyType.multiFamilyResidentialBuilding",
    "Multi-family residential building",
  ],
  ["OTHER", "options.propertyType.other", "Other"],
];

const BUILDING_CONDITION_OPTIONS = [
  ["EXCELLENT", "options.buildingCondition.excellent", "Excellent"],
  ["VERY_GOOD", "options.buildingCondition.veryGood", "Very good"],
  ["GOOD", "options.buildingCondition.good", "Good"],
  ["FAIR_AVERAGE", "options.buildingCondition.fairAverage", "Fair / Average"],
  ["POOR", "options.buildingCondition.poor", "Poor"],
  ["DILAPIDATED", "options.buildingCondition.dilapidated", "Dilapidated"],
  ["OTHER", "options.buildingCondition.other", "Other"],
];

const COMFORT_OPTIONS = [
  ["BASIC", "options.comfortType.basic", "Basic"],
  ["STANDARD", "options.comfortType.standard", "Standard"],
  ["GOOD", "options.comfortType.good", "Good"],
  ["HIGH", "options.comfortType.high", "High"],
  [
    "VERY_HIGH_PREMIUM",
    "options.comfortType.veryHighPremium",
    "Very high / Premium",
  ],
];

const EPC_OPTIONS = ["A+", "A", "B", "C", "D", "E", "F", "G", "H"].map(
  (value) => [value, `options.epc.${value === "A+" ? "A_PLUS" : value}`, value],
);

const HEATING_OPTIONS = [
  ["NONE", "options.heatingType.none", "None"],
  ["GAS_BOILER", "options.heatingType.gasBoiler", "Gas boiler"],
  ["AIR_CONDITION", "options.heatingType.airCondition", "Air-conditioning"],
  ["HEAT_PUMP", "options.heatingType.heatPump", "Heat pump"],
  [
    "DISTRICT_HEATING",
    "options.heatingType.districtHeating",
    "District heating",
  ],
];

const COOLING_OPTIONS = [
  ["NONE", "options.coolingType.none", "None"],
  ["AIR_CONDITION", "options.coolingType.airCondition", "Air-conditioning"],
  ["OTHER", "options.coolingType.other", "Other (specify)"],
  ["UNKNOWN", "options.coolingType.unknown", "Unknown"],
];

const OWNERSHIP_OPTIONS = [
  ["SOLE_OWNER", "options.ownershipType.soleOwner", "Sole owner"],
  [
    "CO_OWNER_SPOUSE_PARTNER",
    "options.ownershipType.coOwnerSpousePartner",
    "Co-owner (with spouse / partner)",
  ],
  [
    "CO_OWNER_OTHER_PERSONS",
    "options.ownershipType.coOwnerOtherPersons",
    "Co-owner (with other persons)",
  ],
  [
    "USUFRUCT_RIGHT_OF_USE",
    "options.ownershipType.usufructRightOfUse",
    "Usufruct / Right of use",
  ],
  ["OTHER", "options.ownershipType.other", "Other"],
];

const SELECT_OPTIONS = {
  property_type: PROPERTY_TYPE_OPTIONS,
  building_condition: BUILDING_CONDITION_OPTIONS,
  has_lift: [
    ["true", "options.yes", "Yes"],
    ["false", "options.no", "No"],
  ],
  comfort_type: COMFORT_OPTIONS,
  year_built: [
    ["0", "options.year.unknown", "Unknown"],
    ...Array.from(
      { length: MAX_BUILD_YEAR - MIN_BUILD_YEAR + 1 },
      (_, index) => {
        const year = String(MAX_BUILD_YEAR - index);
        return [year, year, year];
      },
    ),
  ],
  epc_label: EPC_OPTIONS,
  epc_target_label: EPC_OPTIONS,
  heating_type: HEATING_OPTIONS,
  cooling_type: COOLING_OPTIONS,
  ownership_type: OWNERSHIP_OPTIONS,
};

const PROPERTY_FIELDS = [
  {
    name: "property_type",
    type: "select",
  },
  {
    name: "building_condition",
    type: "select",
  },
  {
    name: "usable_area_sqm",
    suffix: "m²",
    inputMode: "decimal",
  },
  {
    name: "year_built",
    type: "select",
  },
  {
    name: "has_lift",
    type: "select",
  },
  {
    name: "comfort_type",
    type: "select",
  },
  {
    name: "notes",
    type: "textarea",
    full: true,
  },
];

const ADDRESS_FIELDS = [
  { name: "street" },
  { name: "house_number" },
  { name: "city" },
  { name: "postal_code" },
  { name: "apartment_number" },
];

const ENERGY_FIELDS = [
  { name: "epc_label", type: "select" },
  { name: "epc_target_label", type: "select" },
  {
    name: "epc_value",
    suffixKey: "units.kwhPerSqmYear",
    suffixFallback: "kWh/m²/year",
    inputMode: "decimal",
  },
  {
    name: "epc_target_value",
    suffixKey: "units.kwhPerSqmYear",
    suffixFallback: "kWh/m²/year",
    inputMode: "decimal",
  },
  { name: "heating_type", type: "select" },
  { name: "cooling_type", type: "select" },
];

const REGISTRATION_FIELDS = [
  { name: "land_register_id", full: true },
  { name: "ownership_type", type: "select", full: true },
];

function isObject(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function normalizeDecimalInput(value) {
  const cleaned = String(value ?? "")
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "");

  const [whole = "", ...decimalParts] = cleaned.split(".");

  if (!decimalParts.length) {
    return whole;
  }

  return `${whole}.${decimalParts.join("")}`;
}

function normalizeIntegerInput(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function normalizeEpcValue(value) {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();

  if (normalized === "A_PLUS" || normalized === "A PLUS") {
    return "A+";
  }

  return normalized;
}

function normalizeCountryCode(value) {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();

  if (["BELGIUM", "BELGIQUE"].includes(normalized)) return "BE";
  if (["SPAIN", "ESPAÑA", "ESPANA"].includes(normalized)) return "ES";

  return normalized;
}

function isOtherValue(value) {
  return /^OTHER(?::|$)/i.test(String(value ?? "").trim());
}

function readOtherValue(value) {
  const text = String(value ?? "").trim();

  if (!isOtherValue(text)) {
    return "";
  }

  return text.replace(/^OTHER\s*:?\s*/i, "");
}

function getSelectValue(fieldName, value) {
  if (OTHER_FIELDS.has(fieldName) && isOtherValue(value)) {
    return "OTHER";
  }

  if (fieldName === "has_lift") {
    if (value === true || String(value).toLowerCase() === "true") {
      return "true";
    }

    if (value === false || String(value).toLowerCase() === "false") {
      return "false";
    }

    return "";
  }

  if (fieldName === "epc_label" || fieldName === "epc_target_label") {
    return normalizeEpcValue(value);
  }

  if (fieldName === "country_code") {
    return normalizeCountryCode(value);
  }

  if (fieldName === "year_built" && Number(value) === 0) {
    return "0";
  }

  return String(value ?? "")
    .trim()
    .toUpperCase();
}

function normalizePhotos(photos) {
  if (!Array.isArray(photos)) {
    return [];
  }

  return photos
    .map((photo, index) => {
      if (typeof photo === "string") {
        return {
          id: `photo-${index}-${photo}`,
          src: photo,
          fileUrl: photo,
          isProtected: false,
        };
      }

      if (!isObject(photo)) {
        return null;
      }

      const fileUrl = photo.file_url || photo.fileUrl || "";

      const isProtected = Boolean(
        photo.is_protected || photo.file_storage_key || photo.isProtected,
      );

      const src =
        photo.src ||
        photo.url ||
        photo.previewUrl ||
        (!isProtected ? fileUrl : "");

      const id =
        photo.id ??
        photo.photo_id ??
        (src || fileUrl ? `photo-${index}-${src || fileUrl}` : null);

      if (!id) {
        return null;
      }

      return {
        ...photo,
        id,
        src,
        fileUrl,
        isProtected,
      };
    })
    .filter(Boolean);
}

function normalizePropertyInfo(source) {
  const value = isObject(source) ? source : {};

  return {
    ...EMPTY_PROPERTY_INFO,
    id: value.id ?? value.property_id ?? null,
    property_type: value.property_type || "",
    usable_area_sqm:
      value.usable_area_sqm ?? value.surface_sqm ?? value.size_sqm ?? "",
    year_built: value.year_built ?? value.year_of_build ?? "",
    building_condition: value.building_condition || "",
    has_lift:
      value.has_lift === true || value.has_lift === false
        ? value.has_lift
        : value.has_lift || "",
    comfort_type: value.comfort_type || "",
    notes: value.notes || "",
    street: value.street ?? value.street_name ?? "",
    house_number: value.house_number ?? "",
    city: value.city || "",
    postal_code: value.postal_code ?? value.zip_code ?? "",
    country_code: normalizeCountryCode(
      value.country_code ?? value.country ?? "",
    ),
    apartment_number: value.apartment_number || "",
    region: value.region || "",
    full_address: value.full_address || "",
    latitude: value.latitude ?? "",
    longitude: value.longitude ?? "",
    place_id: value.place_id || "",
    epc_label: normalizeEpcValue(
      value.epc_label ?? value.epc_rating_current ?? "",
    ),
    epc_target_label: normalizeEpcValue(
      value.epc_target_label ?? value.epc_rating_target ?? "",
    ),
    epc_value: value.epc_value ?? value.kwh_sqm_year_current ?? "",
    epc_target_value: value.epc_target_value ?? value.kwh_sqm_year_target ?? "",
    heating_type: value.heating_type || "",
    cooling_type: value.cooling_type || "",
    land_register_id: value.land_register_id || "",
    ownership_type: value.ownership_type || "",
    photos: normalizePhotos(value.photos || value.property_photos),
  };
}

function buildFullAddress(propertyInfo) {
  const streetLine = [propertyInfo.street, propertyInfo.house_number]
    .filter(Boolean)
    .join(" ");

  const apartmentPart = propertyInfo.apartment_number
    ? `Apt. ${propertyInfo.apartment_number}`
    : "";

  const localityLine = [propertyInfo.postal_code, propertyInfo.city]
    .filter(Boolean)
    .join(" ");

  const countryLabel = propertyInfo.country_code || "";

  return [streetLine, apartmentPart, localityLine, countryLabel]
    .filter(Boolean)
    .join(", ");
}

function serializePropertyInfo(propertyInfo) {
  const hasLiftValue =
    propertyInfo.has_lift === ""
      ? null
      : propertyInfo.has_lift === true ||
        String(propertyInfo.has_lift).toLowerCase() === "true";

  return {
    property_type: propertyInfo.property_type,
    building_condition: propertyInfo.building_condition,
    usable_area_sqm: propertyInfo.usable_area_sqm,
    year_built: propertyInfo.year_built,
    has_lift: hasLiftValue,
    comfort_type: propertyInfo.comfort_type,
    notes: propertyInfo.notes,
    street: propertyInfo.street,
    house_number: propertyInfo.house_number,
    city: propertyInfo.city,
    postal_code: propertyInfo.postal_code,
    country_code: normalizeCountryCode(propertyInfo.country_code),
    apartment_number: propertyInfo.apartment_number,
    region: propertyInfo.region,
    full_address: propertyInfo.full_address || buildFullAddress(propertyInfo),
    latitude: propertyInfo.latitude,
    longitude: propertyInfo.longitude,
    place_id: propertyInfo.place_id,
    epc_label: propertyInfo.epc_label,
    epc_target_label: propertyInfo.epc_target_label,
    epc_value: propertyInfo.epc_value,
    epc_target_value: propertyInfo.epc_target_value,
    heating_type: propertyInfo.heating_type,
    cooling_type: propertyInfo.cooling_type,
    land_register_id: propertyInfo.land_register_id,
    ownership_type: propertyInfo.ownership_type,
  };
}

function isMissing(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function getValidationErrors(propertyInfo) {
  const errors = {};

  REQUIRED_FIELDS.forEach((fieldName) => {
    if (isMissing(propertyInfo[fieldName])) {
      errors[fieldName] = "validation.required";
    }
  });

  OTHER_FIELDS.forEach((fieldName) => {
    if (
      isOtherValue(propertyInfo[fieldName]) &&
      !readOtherValue(propertyInfo[fieldName])
    ) {
      errors[fieldName] = "validation.otherRequired";
    }
  });

  DECIMAL_FIELDS.forEach((fieldName) => {
    const rawValue = propertyInfo[fieldName];

    if (!isMissing(rawValue) && Number(rawValue) <= 0) {
      errors[fieldName] = "validation.greaterThanZero";
    }
  });

  if (!isMissing(propertyInfo.year_built)) {
    const year = Number(propertyInfo.year_built);

    if (
      year !== 0 &&
      (!Number.isInteger(year) ||
        year < MIN_BUILD_YEAR ||
        year > MAX_BUILD_YEAR)
    ) {
      errors.year_built = "validation.invalidYear";
    }
  }

  if (
    normalizeCountryCode(propertyInfo.country_code) === "ES" &&
    isMissing(propertyInfo.land_register_id)
  ) {
    errors.land_register_id = "validation.required";
  }

  return errors;
}

function getTranslatedOptionLabel(t, fieldName, value) {
  if (OTHER_FIELDS.has(fieldName) && isOtherValue(value)) {
    const specifiedValue = readOtherValue(value);
    const option = (SELECT_OPTIONS[fieldName] || []).find(
      ([optionValue]) => optionValue === "OTHER",
    );
    const otherLabel = option
      ? t(option[1], option[2])
      : t("options.other", "Other");

    return specifiedValue ? `${otherLabel}: ${specifiedValue}` : otherLabel;
  }

  const selectedValue = getSelectValue(fieldName, value);
  const option = (SELECT_OPTIONS[fieldName] || []).find(
    ([optionValue]) => String(optionValue) === selectedValue,
  );

  if (!option) {
    return String(value ?? "");
  }

  const [optionValue, labelKey, fallback] = option;
  return t(labelKey, fallback || optionValue);
}

function loadGoogleMaps(apiKey) {
  if (!apiKey || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise;
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const scriptId = "legup-google-maps-script";
    const existingScript = document.getElementById(scriptId);

    const resolveWhenReady = () => {
      if (window.google?.maps?.places) {
        resolve(window.google);
      } else {
        reject(new Error("Google Maps Places library is unavailable."));
      }
    };

    if (existingScript) {
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }

      existingScript.addEventListener("load", resolveWhenReady, {
        once: true,
      });
      existingScript.addEventListener("error", reject, {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(apiKey)}` +
      "&libraries=places&v=weekly";
    script.async = true;
    script.defer = true;
    script.onload = resolveWhenReady;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

function getAddressPart(place, type, useShortName = false) {
  const component = place?.address_components?.find((item) =>
    item.types?.includes(type),
  );

  if (!component) {
    return "";
  }

  return useShortName ? component.short_name || "" : component.long_name || "";
}

function getDefaultMapCenter(countryCode) {
  const normalizedCountry = normalizeCountryCode(countryCode);

  if (normalizedCountry === "BE") {
    return { lat: 50.8503, lng: 4.3517 };
  }

  if (normalizedCountry === "ES") {
    return { lat: 41.9794, lng: 2.8214 };
  }

  return { lat: 46.2276, lng: 2.2137 };
}

function FieldTooltip({ text, accessibleLabel, forceOpen = false }) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const tooltipId = React.useId();

  if (!text) {
    return null;
  }

  const isOpen = forceOpen || isHovered || isFocused;

  return (
    <span
      className={`lender-property-step__tooltip ${
        isOpen ? "lender-property-step__tooltip--open" : ""
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className="lender-property-step__tooltip-trigger"
        aria-label={accessibleLabel}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsFocused(true);
        }}
      >
        <Info size={14} strokeWidth={2.2} />
      </button>

      <span
        id={tooltipId}
        className="lender-property-step__tooltip-content"
        role="tooltip"
        aria-hidden={!isOpen}
      >
        {text}
      </span>
    </span>
  );
}

function FieldLabel({
  t,
  fieldName,
  htmlFor,
  required,
  labelOverride = "",
  forceTooltipOpen = false,
}) {
  const label =
    labelOverride ||
    t(`fields.${fieldName}`, FIELD_LABEL_FALLBACKS[fieldName] || fieldName);

  const tooltipKey = FIELD_TOOLTIP_KEYS[fieldName];
  const tooltipText = tooltipKey
    ? t(tooltipKey, FIELD_TOOLTIP_FALLBACKS[fieldName] || "")
    : "";

  return (
    <div className="lender-property-step__label-group">
      <label className="lender-property-step__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="lender-property-step__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {tooltipText ? (
        <FieldTooltip
          text={tooltipText}
          accessibleLabel={t(
            "accessibility.fieldInformation",
            "More information",
          )}
          forceOpen={forceTooltipOpen}
        />
      ) : null}
    </div>
  );
}

function Field({
  t,
  name,
  value,
  onChange,
  isEditMode,
  type = "input",
  full = false,
  suffix = "",
  inputMode,
  errorKey = "",
  isRequestMode = false,
  isRequested = false,
  onToggleRequest,
  labelOverride = "",
  datalist = null,
  onTouched,
}) {
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const options = SELECT_OPTIONS[name] || [];
  const required = REQUIRED_FIELDS.has(name);
  const selectedValue = getSelectValue(name, value);
  const otherSelected = OTHER_FIELDS.has(name) && selectedValue === "OTHER";
  const inputId = `lender-property-${name}`;
  const errorId = errorKey ? `${inputId}-error` : undefined;
  const datalistId = datalist?.length ? `${inputId}-suggestions` : undefined;

  function handleInputChange(event) {
    let nextValue = event.target.value;

    if (INTEGER_FIELDS.has(name)) {
      nextValue = normalizeIntegerInput(nextValue);
    } else if (DECIMAL_FIELDS.has(name)) {
      nextValue = normalizeDecimalInput(nextValue);
    }

    onChange(name, nextValue);
  }

  function handleSelectChange(event) {
    const nextValue = event.target.value;

    if (name === "has_lift") {
      onChange(name, nextValue === "" ? "" : nextValue === "true");
      return;
    }

    if (OTHER_FIELDS.has(name) && nextValue === "OTHER") {
      onChange(name, "OTHER:");
      return;
    }

    onChange(name, nextValue);
  }

  const displayValue = (() => {
    if (isMissing(value)) return "—";
    if (type === "select") return getTranslatedOptionLabel(t, name, value);
    return String(value);
  })();

  return (
    <div
      className={`lender-property-step__field ${
        full ? "lender-property-step__field--full" : ""
      } ${errorKey ? "lender-property-step__field--invalid" : ""} ${
        isRequested ? "lender-property-step__field--requested" : ""
      }`}
      onFocusCapture={() => setHasFocusWithin(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setHasFocusWithin(false);
          onTouched?.(name);
        }
      }}
    >
      <div className="lender-property-step__label-row">
        <FieldLabel
          t={t}
          fieldName={name}
          htmlFor={inputId}
          required={required}
          labelOverride={labelOverride}
          forceTooltipOpen={hasFocusWithin}
        />

        {isRequestMode ? (
          <button
            type="button"
            className={`lender-property-step__request-chip ${
              isRequested ? "lender-property-step__request-chip--active" : ""
            }`}
            onClick={onToggleRequest}
          >
            {isRequested ? <Check size={13} strokeWidth={2.6} /> : null}
            <span>
              {isRequested
                ? t("request.selected", "Selected")
                : t("request.add", "Request update")}
            </span>
          </button>
        ) : null}
      </div>

      {isEditMode ? (
        <>
          {type === "select" ? (
            <div className="lender-property-step__select-wrap">
              <select
                id={inputId}
                className="lender-property-step__input lender-property-step__select"
                value={selectedValue}
                onChange={handleSelectChange}
                aria-invalid={Boolean(errorKey)}
                aria-describedby={errorId}
              >
                <option value="">
                  {t("options.select", "Select an option")}
                </option>

                {options.map(([optionValue, labelKey, fallback]) => (
                  <option key={optionValue} value={optionValue}>
                    {t(labelKey, fallback || optionValue)}
                  </option>
                ))}
              </select>

              <ChevronDown
                className="lender-property-step__select-chevron"
                size={17}
                strokeWidth={2}
                aria-hidden="true"
              />
            </div>
          ) : type === "textarea" ? (
            <textarea
              id={inputId}
              className="lender-property-step__input lender-property-step__input--textarea"
              value={value ?? ""}
              onChange={handleInputChange}
              placeholder={t(`placeholders.${name}`, "")}
              aria-invalid={Boolean(errorKey)}
              aria-describedby={errorId}
            />
          ) : suffix ? (
            <div className="lender-property-step__input-with-suffix">
              <input
                id={inputId}
                className="lender-property-step__input lender-property-step__input--with-suffix"
                value={value ?? ""}
                onChange={handleInputChange}
                inputMode={inputMode}
                placeholder={t(`placeholders.${name}`, "")}
                aria-invalid={Boolean(errorKey)}
                aria-describedby={errorId}
              />

              <span className="lender-property-step__suffix">{suffix}</span>
            </div>
          ) : (
            <>
              <input
                id={inputId}
                className="lender-property-step__input"
                value={value ?? ""}
                onChange={handleInputChange}
                inputMode={inputMode}
                placeholder={t(`placeholders.${name}`, "")}
                aria-invalid={Boolean(errorKey)}
                aria-describedby={errorId}
                list={datalistId}
              />

              {datalistId ? (
                <datalist id={datalistId}>
                  {datalist.map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>
              ) : null}
            </>
          )}

          {otherSelected ? (
            <input
              className="lender-property-step__input lender-property-step__other-input"
              value={readOtherValue(value)}
              onChange={(event) =>
                onChange(name, `OTHER: ${event.target.value}`)
              }
              placeholder={t("placeholders.otherSpecify", "Please specify")}
              aria-label={t("placeholders.otherSpecify", "Please specify")}
            />
          ) : null}
        </>
      ) : (
        <div
          className={`lender-property-step__value ${
            type === "textarea" ? "lender-property-step__value--multiline" : ""
          }`}
          title={displayValue === "—" ? undefined : displayValue}
        >
          {displayValue}
          {suffix && displayValue !== "—" ? ` ${suffix}` : ""}
        </div>
      )}

      {errorKey ? (
        <span id={errorId} className="lender-property-step__error" role="alert">
          {t(errorKey, "This field is invalid.")}
        </span>
      ) : null}
    </div>
  );
}

function PropertyCard({ icon: Icon, title, children, area, className = "" }) {
  return (
    <section
      className={`lender-property-step__card lender-property-step__card--${area} ${className}`}
    >
      <div className="lender-property-step__card-header">
        <h3>
          <Icon size={17} strokeWidth={2.2} />
          <span>{title}</span>
        </h3>
      </div>

      {children}
    </section>
  );
}

function AddressSearchField({
  t,
  value,
  onChange,
  inputRef,
  isEditMode,
  mapsErrorKey,
}) {
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const inputId = "lender-property-address-search";

  return (
    <div
      className="lender-property-step__address-search-field"
      onFocusCapture={() => setHasFocusWithin(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setHasFocusWithin(false);
        }
      }}
    >
      <div className="lender-property-step__label-row">
        <div className="lender-property-step__label-group">
          <label className="lender-property-step__label" htmlFor={inputId}>
            {t("fields.searchAddress", "Search address")}
          </label>

          <FieldTooltip
            text={t(
              "descriptions.searchAddress",
              "Search for the property address and select a Google Maps result to fill the address fields automatically.",
            )}
            accessibleLabel={t(
              "accessibility.fieldInformation",
              "More information",
            )}
            forceOpen={hasFocusWithin}
          />
        </div>
      </div>

      {isEditMode ? (
        <div className="lender-property-step__address-search-control">
          <Search size={16} strokeWidth={2.1} />
          <input
            ref={inputRef}
            id={inputId}
            className="lender-property-step__address-search-input"
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            placeholder={t(
              "placeholders.searchAddress",
              "Start typing the property address...",
            )}
            autoComplete="off"
          />
        </div>
      ) : (
        <div className="lender-property-step__value">{value || "—"}</div>
      )}

      {!GOOGLE_MAPS_API_KEY ? (
        <span className="lender-property-step__map-message">
          {t(
            "map.keyMissing",
            "Google Maps is unavailable because the API key is not configured.",
          )}
        </span>
      ) : null}

      {mapsErrorKey ? (
        <span className="lender-property-step__error" role="alert">
          {t(
            mapsErrorKey,
            "Google Maps could not be loaded. You can still enter the address manually.",
          )}
        </span>
      ) : null}
    </div>
  );
}

function PropertyPhotos({
  t,
  photos,
  isEditMode,
  canUpload,
  canDelete,
  isUploading,
  deletingPhotoId,
  error,
  onUpload,
  onDelete,
  onOpen,
}) {
  return (
    <PropertyCard
      icon={ImageIcon}
      title={t("cards.propertyPhotos", "Property photos")}
      area="photos"
      className="lender-property-step__photo-card"
    >
      <div className="lender-property-step__photo-copy">
        <p>
          {t(
            "photos.description",
            "Upload interior and exterior photos to support the technical assessment.",
          )}
        </p>
        <span>{t("labels.optional", "Optional")}</span>
      </div>

      {isEditMode ? (
        <div className="lender-property-step__photo-upload-wrap">
          <input
            id="lender-property-photo-upload"
            className="lender-property-step__photo-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onUpload}
            disabled={!canUpload || isUploading}
          />

          <label
            htmlFor="lender-property-photo-upload"
            className={`lender-property-step__photo-upload ${
              !canUpload || isUploading
                ? "lender-property-step__photo-upload--disabled"
                : ""
            }`}
          >
            {isUploading ? (
              <LoaderCircle
                size={19}
                className="lender-property-step__spinner"
              />
            ) : (
              <UploadCloud size={19} />
            )}

            <span>
              {isUploading
                ? t("photos.uploading", "Uploading...")
                : t("photos.upload", "Upload property photos")}
            </span>
          </label>

          {!canUpload ? (
            <p className="lender-property-step__photo-backend-note">
              {t(
                "photos.backendNotConfigured",
                "Property-photo upload is not connected to the backend yet.",
              )}
            </p>
          ) : null}

          {error ? (
            <p className="lender-property-step__photo-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {photos.length ? (
        <div className="lender-property-step__photo-grid">
          {photos.map((photo, index) => (
            <div key={photo.id} className="lender-property-step__photo-shell">
              <button
                type="button"
                className="lender-property-step__photo-item"
                onClick={() => {
                  if (photo.src) {
                    onOpen(index);
                  }
                }}
                disabled={!photo.src}
              >
                {photo.src ? (
                  <img
                    src={photo.src}
                    alt={`${t("cards.propertyPhotos", "Property photos")} ${index + 1}`}
                    className="lender-property-step__photo-image"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="lender-property-step__photo-loading">
                    <LoaderCircle
                      size={20}
                      className="lender-property-step__spinner"
                    />
                  </span>
                )}
              </button>

              {isEditMode && canDelete ? (
                <button
                  type="button"
                  className="lender-property-step__photo-remove"
                  onClick={() => onDelete(photo)}
                  disabled={String(deletingPhotoId) === String(photo.id)}
                  aria-label={t("photos.remove", "Remove photo")}
                >
                  {String(deletingPhotoId) === String(photo.id) ? (
                    <LoaderCircle
                      size={15}
                      className="lender-property-step__spinner"
                    />
                  ) : (
                    <Trash2 size={15} />
                  )}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="lender-property-step__photo-empty">
          <ImageIcon size={23} strokeWidth={1.8} />
          <span>{t("photos.empty", "No property photos uploaded.")}</span>
        </div>
      )}
    </PropertyCard>
  );
}

export default function LenderManualLoanCasePropertyInfo() {
  const { t } = useTranslation("lender-property-info");
  const context = useOutletContext();

  const {
    loanCaseData,
    reviewData,
    loanCaseId,
    currentStepKey = "property-info",
    stepValues = {},
    isEditMode = true,
    isRequestMode = false,
    revisionDraft,
    addRevisionItem,
    removeRevisionItem,
    handleStepValidityChange,
    handleStepValuesChange,
    validationRequest,
    uploadPropertyPhotos,
    deletePropertyPhoto,
    fetchPropertyPhotoBlob,
  } = context;

  const marketRegion = loanCaseData?.dossier?.market_region;

  const marketCountryCode = marketRegion?.country_code || "";

  const [propertyInfo, setPropertyInfo] = React.useState(EMPTY_PROPERTY_INFO);
  const [previewIndex, setPreviewIndex] = React.useState(null);
  const [isUploadingPhotos, setIsUploadingPhotos] = React.useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = React.useState(null);
  const [photoError, setPhotoError] = React.useState("");
  const [mapsErrorKey, setMapsErrorKey] = React.useState("");
  const [touchedFields, setTouchedFields] = React.useState({});

  function markFieldTouched(fieldName) {
    setTouchedFields((previous) => ({
      ...previous,
      [fieldName]: true,
    }));
  }

  const initializedSourceRef = React.useRef("");
  const propertyInfoRef = React.useRef(propertyInfo);
  const addressInputRef = React.useRef(null);
  const mapContainerRef = React.useRef(null);
  const googleMapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);
  const photoObjectUrlsRef = React.useRef(new Map());

  const sourceIdentifier = String(
    loanCaseId ||
      loanCaseData?.loan_case_id ||
      reviewData?.loan_case_id ||
      reviewData?.id ||
      "property-review",
  );

  React.useEffect(() => {
    propertyInfoRef.current = propertyInfo;
  }, [propertyInfo]);

  React.useEffect(() => {
    if (initializedSourceRef.current === sourceIdentifier) {
      return;
    }

    const storedSection = stepValues?.[currentStepKey]?.property_info;
    const backendSection =
      loanCaseData?.property_info || reviewData?.property_info || null;

    const normalized = normalizePropertyInfo(
      storedSection ?? backendSection ?? EMPTY_PROPERTY_INFO,
    );

    /*
     * Property fields may come from the local unsaved draft, but photos
     * are backend-managed resources. Always rehydrate them from the
     * backend so a stored property_info draft cannot hide uploaded photos.
     */
    const backendPhotos = normalizePhotos(
      backendSection?.photos || backendSection?.property_photos || [],
    );

    if (backendPhotos.length) {
      normalized.photos = backendPhotos;
    }

    normalized.country_code = normalizeCountryCode(marketRegion?.country_code);

    normalized.region = marketRegion?.code || "";

    setTouchedFields({});

    setPropertyInfo(normalized);
    propertyInfoRef.current = normalized;
    initializedSourceRef.current = sourceIdentifier;
  }, [
    currentStepKey,
    loanCaseData,
    reviewData,
    sourceIdentifier,
    stepValues,
    marketRegion,
  ]);

  const validationErrors = React.useMemo(
    () => getValidationErrors(propertyInfo),
    [propertyInfo],
  );

  const validationRequestRef = React.useRef(validationRequest);
  React.useEffect(() => {
    if (validationRequestRef.current === validationRequest) return;
    validationRequestRef.current = validationRequest;
    setTouchedFields(Object.fromEntries(Object.keys(validationErrors).map(key => [key, true])));
  }, [validationRequest, validationErrors]);

  const serializedPropertyInfo = React.useMemo(
    () => serializePropertyInfo(propertyInfo),
    [propertyInfo],
  );

  React.useEffect(() => {
    if (initializedSourceRef.current !== sourceIdentifier) {
      return;
    }

    handleStepValidityChange?.(
      currentStepKey,
      Object.keys(validationErrors).length === 0,
      Object.keys(validationErrors).map(name => t(`fields.${name}`, FIELD_LABEL_FALLBACKS[name] || name)),
    );

    handleStepValuesChange?.(currentStepKey, {
      property_info: serializedPropertyInfo,
    });
  }, [
    currentStepKey,
    handleStepValidityChange,
    handleStepValuesChange,
    serializedPropertyInfo,
    sourceIdentifier,
    validationErrors,
    t,
  ]);

  const requestedItemsByField = React.useMemo(() => {
    const map = new Map();
    const items = revisionDraft?.items;

    if (!Array.isArray(items)) {
      return map;
    }

    items.forEach((item) => {
      const sectionKey = item?.section_key || item?.section;
      const fieldKey = item?.field_key || item?.field;

      if (sectionKey === "property_info" && fieldKey) {
        map.set(fieldKey, item);
      }
    });

    return map;
  }, [revisionDraft]);

  const photos = React.useMemo(
    () => normalizePhotos(propertyInfo.photos),
    [propertyInfo.photos],
  );

  /*
   * New property photos are stored in private storage. The protected
   * endpoint therefore has to be fetched with the Auth0 bearer token;
   * an <img src="..."> request cannot provide that header by itself.
   */
  React.useEffect(() => {
    if (typeof fetchPropertyPhotoBlob !== "function") {
      return undefined;
    }

    const pendingPhotos = normalizePhotos(
      propertyInfoRef.current.photos,
    ).filter((photo) => photo?.id && photo.isProtected && !photo.src);

    if (!pendingPhotos.length) {
      return undefined;
    }

    let cancelled = false;

    async function hydratePhotos() {
      for (const photo of pendingPhotos) {
        try {
          const blob = await fetchPropertyPhotoBlob({
            loanCaseId,
            photoId: photo.id,
          });

          if (cancelled) {
            return;
          }

          const objectUrl = URL.createObjectURL(blob);
          const previousUrl = photoObjectUrlsRef.current.get(String(photo.id));

          if (previousUrl && previousUrl !== objectUrl) {
            URL.revokeObjectURL(previousUrl);
          }

          photoObjectUrlsRef.current.set(String(photo.id), objectUrl);

          setPropertyInfo((previous) => {
            const nextPhotos = normalizePhotos(previous.photos).map((item) =>
              String(item.id) === String(photo.id)
                ? {
                    ...item,
                    src: objectUrl,
                    previewUrl: objectUrl,
                  }
                : item,
            );

            const next = {
              ...previous,
              photos: nextPhotos,
            };

            propertyInfoRef.current = next;
            return next;
          });
        } catch (error) {
          console.error(
            `Failed to load protected property photo ${photo.id}:`,
            error,
          );

          if (!cancelled) {
            setPhotoError(
              error?.message ||
                t(
                  "photos.previewFailed",
                  "A property photo could not be loaded.",
                ),
            );
          }
        }
      }
    }

    hydratePhotos();

    return () => {
      cancelled = true;
    };
  }, [fetchPropertyPhotoBlob, loanCaseId, propertyInfo.photos, t]);

  React.useEffect(() => {
    const objectUrls = photoObjectUrlsRef.current;

    return () => {
      objectUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });

      objectUrls.clear();
    };
  }, []);

  const countryCode = normalizeCountryCode(marketRegion?.country_code);

  function handleChange(fieldName, value) {
    setPropertyInfo((previous) => {
      const next = {
        ...previous,
        [fieldName]: value,
      };

      if (
        [
          "street",
          "house_number",
          "apartment_number",
          "postal_code",
          "city",
          "country_code",
        ].includes(fieldName)
      ) {
        next.full_address = buildFullAddress(next);
      }

      propertyInfoRef.current = next;
      return next;
    });
  }

  function handleAddressSearchChange(value) {
    setPropertyInfo((previous) => {
      const next = {
        ...previous,
        full_address: value,
      };

      propertyInfoRef.current = next;
      return next;
    });
  }

  function toggleRequestedField(fieldName) {
    const existing = requestedItemsByField.get(fieldName);

    if (existing) {
      removeRevisionItem?.(existing.client_id || existing.id);
      return;
    }

    addRevisionItem?.({
      target_type: "FIELD",
      step_code: "PROPERTY",
      section_key: "property_info",
      section_label: t("sections.propertyInformation", "Property information"),
      field_key: fieldName,
      field_label: t(
        `fields.${fieldName}`,
        FIELD_LABEL_FALLBACKS[fieldName] || fieldName,
      ),
    });
  }

  React.useEffect(() => {
    let cancelled = false;
    let placeChangedListener = null;

    async function initializeGoogleMaps() {
      if (!GOOGLE_MAPS_API_KEY || !mapContainerRef.current) {
        return;
      }

      try {
        const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

        if (cancelled || !google || !mapContainerRef.current) {
          return;
        }

        const current = propertyInfoRef.current;
        const hasCoordinates =
          Number.isFinite(Number(current.latitude)) &&
          Number.isFinite(Number(current.longitude)) &&
          current.latitude !== "" &&
          current.longitude !== "";

        const center = hasCoordinates
          ? {
              lat: Number(current.latitude),
              lng: Number(current.longitude),
            }
          : getDefaultMapCenter(current.country_code);

        googleMapRef.current = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom: hasCoordinates ? 16 : 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });

        markerRef.current = new google.maps.Marker({
          map: googleMapRef.current,
          position: center,
          visible: hasCoordinates,
        });

        if (!addressInputRef.current) {
          return;
        }

        autocompleteRef.current = new google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            fields: [
              "formatted_address",
              "geometry",
              "place_id",
              "address_components",
            ],
            types: ["address"],
            componentRestrictions: {
              componentRestrictions: {
                country: marketCountryCode
                  ? marketCountryCode.toLowerCase()
                  : ["be", "es"],
              },
            },
          },
        );

        placeChangedListener = autocompleteRef.current.addListener(
          "place_changed",
          () => {
            const place = autocompleteRef.current?.getPlace();

            if (!place?.geometry?.location) {
              return;
            }

            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();
            const selectedCountryCode = normalizeCountryCode(
              getAddressPart(place, "country", true),
            );
            const selectedCity =
              getAddressPart(place, "locality") ||
              getAddressPart(place, "postal_town") ||
              getAddressPart(place, "administrative_area_level_2") ||
              getAddressPart(place, "administrative_area_level_1");

            if (
              marketCountryCode &&
              selectedCountryCode !== normalizeCountryCode(marketCountryCode)
            ) {
              setMapsErrorKey("map.countryMismatch");

              return;
            }

            setPropertyInfo((previous) => {
              const next = {
                ...previous,
                street:
                  getAddressPart(place, "route") ||
                  getAddressPart(place, "street_address"),
                house_number: getAddressPart(place, "street_number"),
                city: selectedCity,
                postal_code: getAddressPart(place, "postal_code"),
                country_code: normalizeCountryCode(marketCountryCode),
                region: marketRegion?.code || "",
                full_address: place.formatted_address || "",
                latitude: String(latitude),
                longitude: String(longitude),
                place_id: place.place_id || "",
              };

              propertyInfoRef.current = next;
              return next;
            });

            const position = {
              lat: latitude,
              lng: longitude,
            };

            googleMapRef.current?.setCenter(position);
            googleMapRef.current?.setZoom(16);
            markerRef.current?.setPosition(position);
            markerRef.current?.setVisible(true);
          },
        );
      } catch (error) {
        console.error("Failed to initialise Google Maps:", error);

        if (!cancelled) {
          setMapsErrorKey("map.loadFailed");
        }
      }
    }

    initializeGoogleMaps();

    return () => {
      cancelled = true;
      placeChangedListener?.remove?.();
      autocompleteRef.current = null;
      markerRef.current = null;
      googleMapRef.current = null;
    };
  }, [sourceIdentifier]);

  React.useEffect(() => {
    const autocomplete = autocompleteRef.current;

    if (!autocomplete?.setComponentRestrictions) {
      return;
    }

    autocomplete.setComponentRestrictions({
      country: countryCode ? countryCode.toLowerCase() : ["be", "es"],
    });
  }, [countryCode]);

  React.useEffect(() => {
    if (!googleMapRef.current || !markerRef.current) {
      return;
    }

    const latitude = Number(propertyInfo.latitude);
    const longitude = Number(propertyInfo.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    if (propertyInfo.latitude === "" || propertyInfo.longitude === "") {
      return;
    }

    const position = {
      lat: latitude,
      lng: longitude,
    };

    googleMapRef.current.setCenter(position);
    googleMapRef.current.setZoom(16);
    markerRef.current.setPosition(position);
    markerRef.current.setVisible(true);
  }, [propertyInfo.latitude, propertyInfo.longitude]);

  async function handlePhotoUpload(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length || typeof uploadPropertyPhotos !== "function") {
      return;
    }

    try {
      setIsUploadingPhotos(true);
      setPhotoError("");

      const response = await uploadPropertyPhotos({
        loanCaseId,
        propertyId: propertyInfo.id,
        files,
      });

      const uploadedPhotos = normalizePhotos(
        response?.photos || response?.results || response,
      );

      setPropertyInfo((previous) => {
        const next = {
          ...previous,
          photos: [...normalizePhotos(previous.photos), ...uploadedPhotos],
        };

        propertyInfoRef.current = next;
        return next;
      });
    } catch (error) {
      console.error("Failed to upload property photos:", error);
      setPhotoError(
        error?.message ||
          t("photos.uploadFailed", "Photos could not be uploaded."),
      );
    } finally {
      setIsUploadingPhotos(false);
    }
  }

  async function handlePhotoDelete(photo) {
    if (typeof deletePropertyPhoto !== "function") {
      return;
    }

    try {
      setDeletingPhotoId(photo.id);
      setPhotoError("");

      await deletePropertyPhoto({
        loanCaseId,
        propertyId: propertyInfo.id,
        photoId: photo.id,
        photo,
      });

      const objectUrl = photoObjectUrlsRef.current.get(String(photo.id));

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        photoObjectUrlsRef.current.delete(String(photo.id));
      }

      setPropertyInfo((previous) => {
        const next = {
          ...previous,
          photos: normalizePhotos(previous.photos).filter(
            (item) => String(item.id) !== String(photo.id),
          ),
        };

        propertyInfoRef.current = next;
        return next;
      });
    } catch (error) {
      console.error("Failed to remove property photo:", error);
      setPhotoError(
        error?.message ||
          t("photos.removeFailed", "The photo could not be removed."),
      );
    } finally {
      setDeletingPhotoId(null);
    }
  }

  React.useEffect(() => {
    if (previewIndex === null) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setPreviewIndex(null);
      }

      if (event.key === "ArrowLeft") {
        setPreviewIndex((current) =>
          current === null
            ? null
            : (current - 1 + photos.length) % photos.length,
        );
      }

      if (event.key === "ArrowRight") {
        setPreviewIndex((current) =>
          current === null ? null : (current + 1) % photos.length,
        );
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [photos.length, previewIndex]);

  function renderFields(fields) {
    return fields.map((field) => {
      const fieldName = field.name;
      const labelOverride =
        fieldName === "land_register_id"
          ? t("fields.cadastralReference", "Cadastral reference")
          : "";

      return (
        <Field
          key={fieldName}
          t={t}
          {...field}
          labelOverride={labelOverride}
          suffix={
            field.suffixKey
              ? t(field.suffixKey, field.suffixFallback || "")
              : field.suffix || ""
          }
          value={propertyInfo[fieldName]}
          onChange={handleChange}
          isEditMode={isEditMode}
          errorKey={touchedFields[fieldName] ? validationErrors[fieldName] : ""}
          onTouched={markFieldTouched}
          isRequestMode={isRequestMode}
          isRequested={requestedItemsByField.has(fieldName)}
          onToggleRequest={() => toggleRequestedField(fieldName)}
        />
      );
    });
  }

  return (
    <div className="lender-property-step">
      <div className="lender-property-step__card-grid">
        <PropertyCard
          icon={Home}
          title={t("cards.propertyDetails", "Property information")}
          area="property"
        >
          <div className="lender-property-step__fields">
            {renderFields(PROPERTY_FIELDS)}
          </div>
        </PropertyCard>

        <PropertyCard
          icon={MapPin}
          title={t("cards.propertyAddress", "Address")}
          area="address"
        >
          <div className="lender-property-step__address-content">
            <AddressSearchField
              t={t}
              value={propertyInfo.full_address}
              onChange={handleAddressSearchChange}
              inputRef={addressInputRef}
              isEditMode={isEditMode}
              mapsErrorKey={mapsErrorKey}
            />

            <div className="lender-property-step__map-wrap">
              {GOOGLE_MAPS_API_KEY ? (
                <div
                  ref={mapContainerRef}
                  className="lender-property-step__map-frame"
                  aria-label={t("map.title", "Property location")}
                />
              ) : (
                <div className="lender-property-step__map-empty">
                  <MapPin size={23} strokeWidth={1.8} />
                  <span>
                    {t(
                      "map.empty",
                      "Enter the property address to display its location on the map.",
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="lender-property-step__fields lender-property-step__fields--address">
              {renderFields(ADDRESS_FIELDS)}
            </div>
          </div>
        </PropertyCard>

        <PropertyCard
          icon={Zap}
          title={t("cards.energyPerformance", "Energetic details")}
          area="energy"
        >
          <div className="lender-property-step__fields">
            {renderFields(ENERGY_FIELDS)}
          </div>
        </PropertyCard>

        <PropertyCard
          icon={Landmark}
          title={t("cards.registrationOwnership", "Registration & cadaster")}
          area="registration"
        >
          <div className="lender-property-step__fields lender-property-step__fields--registration">
            {renderFields(
              REGISTRATION_FIELDS.filter(
                (field) =>
                  field.name !== "land_register_id" || countryCode === "ES",
              ),
            )}
          </div>
        </PropertyCard>
      </div>

      <PropertyPhotos
        t={t}
        photos={photos}
        isEditMode={isEditMode}
        canUpload={typeof uploadPropertyPhotos === "function"}
        canDelete={typeof deletePropertyPhoto === "function"}
        isUploading={isUploadingPhotos}
        deletingPhotoId={deletingPhotoId}
        error={photoError}
        onUpload={handlePhotoUpload}
        onDelete={handlePhotoDelete}
        onOpen={setPreviewIndex}
      />

      {previewIndex !== null && photos[previewIndex] ? (
        <div
          className="lender-property-step__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t("photos.preview", "Property photo preview")}
          onClick={() => setPreviewIndex(null)}
        >
          <div
            className="lender-property-step__lightbox-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="lender-property-step__lightbox-close"
              onClick={() => setPreviewIndex(null)}
              aria-label={t("photos.close", "Close preview")}
            >
              <X size={21} />
            </button>

            {photos.length > 1 ? (
              <button
                type="button"
                className="lender-property-step__lightbox-arrow lender-property-step__lightbox-arrow--left"
                onClick={() =>
                  setPreviewIndex(
                    (previewIndex - 1 + photos.length) % photos.length,
                  )
                }
                aria-label={t("photos.previous", "Previous photo")}
              >
                <ChevronLeft size={24} />
              </button>
            ) : null}

            <img
              className="lender-property-step__lightbox-image"
              src={photos[previewIndex].src}
              alt={`${t("cards.propertyPhotos", "Property photos")} ${previewIndex + 1}`}
            />

            {photos.length > 1 ? (
              <button
                type="button"
                className="lender-property-step__lightbox-arrow lender-property-step__lightbox-arrow--right"
                onClick={() =>
                  setPreviewIndex((previewIndex + 1) % photos.length)
                }
                aria-label={t("photos.next", "Next photo")}
              >
                <ChevronRight size={24} />
              </button>
            ) : null}

            <span className="lender-property-step__lightbox-counter">
              {previewIndex + 1} / {photos.length}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
